// Shared debounced-autosave primitives for the single-page CNE editor.
//
// AutosaveTextarea / AutosaveInput accept an initial value + onSave
// callback, debounce keystrokes for 1.2s, and surface a tiny saving /
// saved / error indicator inline.
//
// SOURCE-OF-TRUTH MODEL (changed 2026-05 — fixes typed text reverting
// and spelling getting scrambled):
//   The `value` prop SEEDS the field exactly once, on mount. After that
//   the local `text` state is the single source of truth. We deliberately
//   do NOT re-sync from `value` on later renders.
//
//   Why: every keystroke autosave invalidates the encounter query, which
//   refetches the whole encounter. Those refetches land out of order — a
//   refetch kicked off by an EARLIER save carries server text that is a
//   few keystrokes behind. The old code re-applied that stale value with
//   a `useEffect([value])`, which (a) reverted the doctor's newest words
//   and (b) reset the textarea caret mid-word, so the next keystrokes
//   landed in the wrong place and scrambled the spelling.
//
//   When a DIFFERENT encounter is opened, EncounterEditor remounts this
//   component (it keys the editor body on the encounter id), so the
//   field re-seeds cleanly from the new encounter without any in-render
//   re-sync.
//
// Imperative API exposed via ref:
//   flush()         — commit any pending debounced save immediately
//                     (parent calls this before Save draft / Submit).
//   insert(snippet) — append a clinical snippet to the CURRENT local
//                     text and schedule a save. Used by SnippetPicker;
//                     appending to the live text means it never discards
//                     unsaved keystrokes.

import { forwardRef, useImperativeHandle, useRef, useState } from 'react'

const DEBOUNCE_MS = 1200

// --------------------------------------------------------------------------- //
// Indicator
// --------------------------------------------------------------------------- //

function SavingIndicator({ state }) {
  if (state === 'saving') return <span className="text-[11px] text-slate-400">Saving…</span>
  if (state === 'saved')  return <span className="text-[11px] text-emerald-600">Saved</span>
  if (state === 'error')  return <span className="text-[11px] text-rose-600">Save failed</span>
  return <span className="text-[11px] text-slate-300">Auto-saves</span>
}

// --------------------------------------------------------------------------- //
// Section wrapper — one labelled block in the long scroll.
// --------------------------------------------------------------------------- //

export function Section({ title, hint, children, id }) {
  return (
    // `scroll-mt-24` gives anchor jumps (sidebar nav + native #anchor
    // links) a ~6rem top offset so the section title doesn't disappear
    // under any sticky header on smooth scroll.
    <section id={id} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm scroll-mt-24">
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <h3 className="font-bold text-slate-800 text-base">{title}</h3>
        {hint && <span className="text-[11px] text-slate-400">{hint}</span>}
      </div>
      {children}
    </section>
  )
}

// --------------------------------------------------------------------------- //
// AutosaveTextarea — multi-line debounced field
// --------------------------------------------------------------------------- //

export const AutosaveTextarea = forwardRef(function AutosaveTextarea(
  { value = '', onSave, disabled, rows = 3, placeholder = '', label, sublabel, saveState = 'idle' },
  ref,
) {
  // Seed once. Local `text` is authoritative thereafter — see the
  // SOURCE-OF-TRUTH MODEL note at the top of this file.
  const [text, setText] = useState(value || '')
  // Last value already handed to onSave — a ref purely for de-duping,
  // so it never causes a render of its own.
  const lastSavedRef = useRef(value || '')
  const timerRef = useRef(null)

  const commit = (next) => {
    if (next === lastSavedRef.current) return
    lastSavedRef.current = next
    onSave?.(next)
  }

  const schedule = (next) => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      timerRef.current = null
      commit(next)
    }, DEBOUNCE_MS)
  }

  const flush = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null }
    commit(text)
  }

  const handleChange = (next) => {
    setText(next)
    schedule(next)
  }

  // Append a snippet to the live local text, then schedule a save.
  const insert = (snippet) => {
    if (!snippet) return
    const next = text ? `${text}\n${snippet}` : snippet
    setText(next)
    schedule(next)
  }

  useImperativeHandle(ref, () => ({ flush, insert }), [text])

  return (
    <div>
      {label && (
        <div className="flex items-baseline justify-between mb-1">
          <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
            {label}
            {sublabel && <span className="ml-1.5 text-[10px] font-normal text-slate-400 normal-case tracking-normal">{sublabel}</span>}
          </label>
          <SavingIndicator state={saveState} />
        </div>
      )}
      <textarea
        rows={rows}
        value={text}
        disabled={disabled}
        onChange={(e) => handleChange(e.target.value)}
        onBlur={flush}
        placeholder={placeholder}
        className="w-full border border-slate-300 focus:border-wantok-600 focus:outline-none rounded-lg px-3 py-2 text-sm leading-relaxed disabled:bg-slate-50"
      />
    </div>
  )
})

// --------------------------------------------------------------------------- //
// AutosaveInput — single-line short field (e.g. AMPLE last meal)
// --------------------------------------------------------------------------- //

export const AutosaveInput = forwardRef(function AutosaveInput(
  { value = '', onSave, disabled, placeholder = '', label, sublabel, saveState = 'idle' },
  ref,
) {
  // Seed once; same source-of-truth model as AutosaveTextarea.
  const [text, setText] = useState(value || '')
  const lastSavedRef = useRef(value || '')
  const timerRef = useRef(null)

  const commit = (next) => {
    if (next === lastSavedRef.current) return
    lastSavedRef.current = next
    onSave?.(next)
  }

  const schedule = (next) => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      timerRef.current = null
      commit(next)
    }, DEBOUNCE_MS)
  }

  const flush = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null }
    commit(text)
  }

  const handleChange = (next) => {
    setText(next)
    schedule(next)
  }

  const insert = (snippet) => {
    if (!snippet) return
    const next = text ? `${text}\n${snippet}` : snippet
    setText(next)
    schedule(next)
  }

  useImperativeHandle(ref, () => ({ flush, insert }), [text])

  return (
    <div>
      {label && (
        <div className="flex items-baseline justify-between mb-1">
          <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
            {label}
            {sublabel && <span className="ml-1.5 text-[10px] font-normal text-slate-400 normal-case tracking-normal">{sublabel}</span>}
          </label>
          <SavingIndicator state={saveState} />
        </div>
      )}
      <input
        type="text"
        value={text}
        disabled={disabled}
        onChange={(e) => handleChange(e.target.value)}
        onBlur={flush}
        placeholder={placeholder}
        className="w-full border border-slate-300 focus:border-wantok-600 focus:outline-none rounded-lg px-3 py-2 text-sm disabled:bg-slate-50"
      />
    </div>
  )
})
